import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from 'auth/AuthProvider';
import { PERMISSIONS } from 'auth/permissions';
import { queryKeys } from 'core/api/queryKeys';
import { firstFieldError, humanizeApiError } from 'core/errors/mapFieldErrors';
import { useI18n } from 'core/i18n/I18nProvider';
import { paths } from 'core/routing/paths';
import { companyPlansApi } from 'features/company-plans/companyPlans.api';
import {
  PAYMENT_PROVIDERS,
  PLAN_PURCHASE_STATUSES,
  canConfirmPurchasePayment,
  companyDisplayName,
  invalidateCompanyPlanQueries,
  listItems,
  listMeta,
  planDisplayName,
  mapPurchaseConfirmErrors,
  purchaseSnapshot,
  statusValue,
} from 'features/company-plans/companyPlans.helpers';
import { AdminActionDialog, handleMutationError, StatusChip } from 'features/company-plans/CompanyPlanUi';
import { formatMoney } from 'features/company-plans/money';
import { Button } from 'shared/components/Button';
import { EmptyState, ErrorState, PageHeader, Skeleton } from 'shared/components/Feedback';
import { FilterBar } from 'shared/components/FilterBar';
import { ListTable } from 'shared/components/ListTable';
import { detailsAction, RowActionsCell, RowActionsHeader } from 'shared/components/RowActionsMenu';
import { Pagination } from 'shared/components/Pagination';
import { useToast } from 'shared/components/Toast';
import { SelectField, TextArea, TextField } from 'shared/forms/Fields';
import { formatDateTime } from 'shared/utilities/format';

export function PurchasesPage() {
  const { t, locale, dir } = useI18n();
  const [filters, setFilters] = useState({
    page: 1,
    per_page: 15,
    company_id: '',
    plan_id: '',
    status: '',
    payment_provider: '',
    created_from: '',
    created_to: '',
  });
  const query = useQuery({
    queryKey: queryKeys.planPurchases(filters),
    queryFn: ({ signal }) => companyPlansApi.listPurchases(filters, { signal }),
  });
  const items = listItems(query.data);
  const meta = listMeta(query.data);

  if (query.isLoading) return <Skeleton rows={6} />;
  if (query.isError) {
    return <ErrorState title={humanizeApiError(query.error, t)} onRetry={query.refetch} retryLabel={t('actions.retry')} />;
  }

  return (
    <div>
      <PageHeader title={t('companyPlans.purchasesTitle')} />
      <FilterBar
        resetLabel={t('actions.reset')}
        onReset={() => setFilters({
          page: 1, per_page: 15, company_id: '', plan_id: '', status: '', payment_provider: '', created_from: '', created_to: '',
        })}
      >
        <TextField id="purchase-company" label={t('companyPlans.companyName')} value={filters.company_id} onChange={(value) => setFilters((current) => ({ ...current, company_id: value, page: 1 }))} />
        <TextField id="purchase-plan" label={t('nav.companyPlans')} value={filters.plan_id} onChange={(value) => setFilters((current) => ({ ...current, plan_id: value, page: 1 }))} />
        <SelectField
          id="purchase-status"
          label={t('companyPlans.paymentStatus')}
          value={filters.status}
          onChange={(value) => setFilters((current) => ({ ...current, status: value, page: 1 }))}
          options={[{ value: '', label: t('filters.all') }, ...PLAN_PURCHASE_STATUSES.map((status) => ({ value: status, label: t(`companyPlans.statuses.${status}`) }))]}
        />
        <SelectField
          id="purchase-provider"
          label={t('companyPlans.paymentProvider')}
          value={filters.payment_provider}
          onChange={(value) => setFilters((current) => ({ ...current, payment_provider: value, page: 1 }))}
          options={[{ value: '', label: t('filters.all') }, ...PAYMENT_PROVIDERS.map((provider) => ({ value: provider, label: t(`companyPlans.providers.${provider}`) }))]}
        />
        <TextField id="purchase-from" label={t('companyPlans.dateFrom')} type="date" value={filters.created_from} onChange={(value) => setFilters((current) => ({ ...current, created_from: value, page: 1 }))} />
        <TextField id="purchase-to" label={t('companyPlans.dateTo')} type="date" value={filters.created_to} onChange={(value) => setFilters((current) => ({ ...current, created_to: value, page: 1 }))} />
      </FilterBar>
      {items.length === 0 ? <EmptyState title={t('empty.purchases')} /> : (
        <ListTable caption={t('companyPlans.purchasesTitle')} stickyActions>
          <thead>
            <tr>
              <th>ID</th>
              <th>{t('companyPlans.companyName')}</th>
              <th>{t('nav.companyPlans')}</th>
              <th>{t('companyPlans.paymentStatus')}</th>
              <th>{t('companyPlans.price')}</th>
              <th>{t('companyPlans.createdAt')}</th>
              <RowActionsHeader />
            </tr>
          </thead>
          <tbody>
            {items.map((purchase) => (
              <tr key={purchase.id}>
                <td>{purchase.id}</td>
                <td>{companyDisplayName(purchase.company)}</td>
                <td>{planDisplayName(purchase.plan || purchase, locale)}</td>
                <td><StatusChip status={purchase.status} t={t} /></td>
                <td>{formatMoney(purchase.total_minor ?? purchase.unit_price_minor, purchase.currency, locale)}</td>
                <td>{formatDateTime(purchase.created_at, locale)}</td>
                <RowActionsCell
                  label={`${t('actions.rowActions')} #${purchase.id}`}
                  items={[detailsAction(paths.planPurchaseDetail(purchase.id), t)]}
                />
              </tr>
            ))}
          </tbody>
        </ListTable>
      )}
      <Pagination
        page={meta.current_page || filters.page}
        lastPage={meta.last_page}
        total={meta.total}
        onPageChange={(page) => setFilters((current) => ({ ...current, page }))}
        label={t('pagination.page')}
        ofLabel={t('pagination.of')}
        previousLabel={t('pagination.previous')}
        nextLabel={t('pagination.next')}
        dir={dir}
      />
    </div>
  );
}

export function PurchaseDetailPage() {
  const { id } = useParams();
  const { t, locale } = useI18n();
  const { hasPermission } = useAuth();
  const canManage = hasPermission(PERMISSIONS.purchasesManage);
  const toast = useToast();
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [errors, setErrors] = useState({});
  const query = useQuery({
    queryKey: queryKeys.planPurchase(id),
    queryFn: ({ signal }) => companyPlansApi.showPurchase(id, { signal }),
  });

  const confirmPayment = useMutation({
    mutationFn: (body) => companyPlansApi.confirmPurchasePayment(id, body),
    onSuccess: async () => {
      setDialogOpen(false);
      setReference('');
      setNote('');
      setErrors({});
      await invalidateCompanyPlanQueries(queryClient);
      await queryClient.invalidateQueries({ queryKey: queryKeys.planPurchase(id) });
      toast.success(t('status.updated'));
    },
    onError: (error) => {
      handleMutationError(error, {
        t,
        toast,
        setErrors: (next) => setErrors(mapPurchaseConfirmErrors(next)),
      });
    },
  });

  if (query.isLoading) return <Skeleton rows={6} />;
  if (query.isError) {
    return <ErrorState title={humanizeApiError(query.error, t)} onRetry={query.refetch} retryLabel={t('actions.retry')} />;
  }

  const purchase = query.data;
  const snapshot = purchaseSnapshot(purchase);
  const events = purchase.payment_events || purchase.events || [];
  const showConfirm = canManage && canConfirmPurchasePayment(purchase);

  const submitConfirmation = () => {
    if (confirmPayment.isPending) return;
    const nextErrors = {};
    if (!reference.trim()) nextErrors.payment_reference = [t('companyPlans.validation.paymentReference')];
    if (!note.trim()) nextErrors.payment_confirmation_note = [t('companyPlans.validation.adminNote')];
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }
    setErrors({});
    confirmPayment.mutate({
      reference: reference.trim(),
      note: note.trim(),
    });
  };

  return (
    <div>
      <PageHeader
        title={`${t('companyPlans.purchaseDetail')} #${purchase.id}`}
        backTo={paths.planPurchases}
        actions={showConfirm ? (
          <Button onClick={() => setDialogOpen(true)}>{t('actions.confirmPayment')}</Button>
        ) : null}
      />
      <h2>{t('companyPlans.snapshot')}</h2>
      <dl className="definition-grid">
        <div className="definition-row"><dt>{t('companyPlans.nameAr')}</dt><dd>{snapshot.name_ar || '—'}</dd></div>
        <div className="definition-row"><dt>{t('companyPlans.nameEn')}</dt><dd>{snapshot.name_en || '—'}</dd></div>
        <div className="definition-row"><dt>{t('companyPlans.attemptQuantity')}</dt><dd>{snapshot.attempt_quantity ?? purchase.attempt_quantity ?? '—'}</dd></div>
        <div className="definition-row"><dt>{t('companyPlans.price')}</dt><dd>{formatMoney(purchase.total_minor ?? snapshot.unit_price_minor, purchase.currency, locale)}</dd></div>
        <div className="definition-row"><dt>{t('companyPlans.currency')}</dt><dd>{purchase.currency || '—'}</dd></div>
        <div className="definition-row"><dt>{t('companyPlans.paymentStatus')}</dt><dd><StatusChip status={purchase.status} t={t} /></dd></div>
        <div className="definition-row"><dt>{t('companyPlans.paymentProvider')}</dt><dd>{t(`companyPlans.providers.${purchase.payment_provider || 'manual'}`)}</dd></div>
        <div className="definition-row"><dt>{t('companyPlans.paymentReference')}</dt><dd>{purchase.payment_reference || '—'}</dd></div>
        <div className="definition-row"><dt>{t('companyPlans.entitlementCreated')}</dt><dd>{purchase.entitlement?.id ? <Link to={paths.companyEntitlementDetail(purchase.entitlement.id)}>#{purchase.entitlement.id}</Link> : '—'}</dd></div>
        <div className="definition-row"><dt>{t('companyPlans.createdAt')}</dt><dd>{formatDateTime(purchase.created_at, locale)}</dd></div>
        <div className="definition-row"><dt>{t('companyPlans.paidAt')}</dt><dd>{formatDateTime(purchase.paid_at, locale)}</dd></div>
        <div className="definition-row"><dt>{t('companyPlans.companyName')}</dt><dd>{purchase.company ? <Link to={paths.companyDetail(purchase.company.id)}>{companyDisplayName(purchase.company)}</Link> : '—'}</dd></div>
      </dl>
      <h2>{t('companyPlans.paymentEvents')}</h2>
      {events.length === 0 ? <p className="muted">{t('empty.generic')}</p> : (
        <ListTable caption={t('companyPlans.paymentEvents')}>
          <thead>
            <tr>
              <th>{t('companyPlans.status')}</th>
              <th>{t('companyPlans.paymentReference')}</th>
              <th>{t('companyPlans.adminNote')}</th>
              <th>{t('companyPlans.createdAt')}</th>
            </tr>
          </thead>
          <tbody>
            {events.map((event) => (
              <tr key={event.id || `${event.type}-${event.created_at}`}>
                <td>{statusValue(event.type || event.status)}</td>
                <td>{event.reference || '—'}</td>
                <td>{event.note || '—'}</td>
                <td>{formatDateTime(event.created_at, locale)}</td>
              </tr>
            ))}
          </tbody>
        </ListTable>
      )}
      <AdminActionDialog
        open={dialogOpen}
        title={t('companyPlans.confirmPaymentTitle')}
        body={t('companyPlans.confirmPaymentBody')}
        confirmLabel={t('companyPlans.confirmPaymentSubmit')}
        cancelLabel={t('actions.cancel')}
        tone="primary"
        loading={confirmPayment.isPending}
        onCancel={() => {
          if (confirmPayment.isPending) return;
          setDialogOpen(false);
        }}
        onConfirm={submitConfirmation}
      >
        <TextField
          id="payment_reference"
          label={t('companyPlans.transactionReference')}
          required
          value={reference}
          onChange={setReference}
          error={firstFieldError(errors, 'payment_reference')}
        />
        <TextArea
          id="payment_confirmation_note"
          label={t('companyPlans.adminNote')}
          required
          value={note}
          onChange={setNote}
          error={firstFieldError(errors, 'payment_confirmation_note')}
        />
      </AdminActionDialog>
    </div>
  );
}
